import React, { Fragment, useMemo, FunctionComponent } from "react"
import {
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    Badge
} from "reactstrap"
import { isAdmin, User } from "../../data/model/user-model"
import { isAuthorized } from "../ProtectedRoute"
import { useSelector } from "react-redux"
import { NavMenuProps } from "./NavMenuProps"
import { ApplicationState } from "../../core/types"
import { ConnectedRouterProps } from "react-router-redux"
import { AlarmsStatus, calcAlarmsStatus } from "../../data/stores/alarm/alarm-store.helper"
import { AlarmModel } from "../../data/model/alarm-model"
import { ModalShowNotifications } from "./notifications/ModalShowNotifications"
import { isCloudApi } from "src/data/api"
import { SettingsNavMenuItem } from "./SettingsNavMenuItem"
import SettingsIcon from "../navbar/assets/gear.svg";
export type AllNavProps = NavMenuProps & ConnectedRouterProps<ApplicationState>

export type SettingsPageUrl =
        "general" |
        "instruments" |
        "notifications" |
        "users" |
        "updates" |
        "api-keys" |
        "aws-analysis-settings" |
        "audit-logs" |
        "about" |
        "set-instrument-login" |
        "instrument-login-management" |
        "communications" |
        "register"

const ADMIN_ONLY_SETTINGS: SettingsPageUrl[] = ["users", "api-keys", "audit-logs", "instrument-login-management", "register"]
const ADMIN_OR_LABTECH_ONLY_SETTINGS: SettingsPageUrl[] = ["set-instrument-login"]
const NON_CLOUD_SETTINGS: SettingsPageUrl[] = ["updates", "register"]
const ONLY_CLOUD_SETTINGS: SettingsPageUrl[] = ["api-keys", "aws-analysis-settings"]

export const canViewSettingsPage = (user: User, page: SettingsPageUrl): boolean => {
    if ( isCloudApi() && NON_CLOUD_SETTINGS.includes(page) ) {
        return false
    }
    if ( !isCloudApi() && ONLY_CLOUD_SETTINGS.includes(page) ) {
        return false
    }
    if ( ADMIN_OR_LABTECH_ONLY_SETTINGS.includes(page) && !isAuthorized(user, "labtech") ) {
        return false
    }
    return user && isAdmin(user) || !ADMIN_ONLY_SETTINGS.includes(page)
}

const badgeFactorsMapping = {
    about: ["needsChemistryBundleUpdate", "needsUiBundleUpdate"]
}

const getBadgeCount = (user: User, props: AllNavProps, badgeFactors?: Array<any>) => {
    if (!isAuthorized(props.user, "admin")) { return 0 }
    if (!badgeFactors) {
        badgeFactors = Object.values(badgeFactorsMapping).reduce((total, v) =>
            total.concat(v)
        )
    }
    return badgeFactors.reduce((total, v) => (props[v] ? total + 1 : total), 0)
}

export const SettingsNavMenu: FunctionComponent<AllNavProps> = (props: AllNavProps) => {

    const alarms = useSelector<ApplicationState,AlarmModel[]>( state => state.alarm.alarms )
    const clearedAlarmIds = useSelector<ApplicationState,string[]>( state => state.alarm.clearedAlarmIds)
    const error = useSelector<ApplicationState,Error>( state => state.alarm.error )

    const totalBadgeCount = getBadgeCount(props.user, props)
    const aboutBadgeCount = getBadgeCount(props.user, props, badgeFactorsMapping["about"])

    const alarmsStatus: AlarmsStatus = useMemo( () => {
        return calcAlarmsStatus(alarms, clearedAlarmIds)
    }, [alarms, clearedAlarmIds] )

    return (
        <Fragment>
            {!error && alarmsStatus.alarmsCount >= 0 && (
                <UncontrolledDropdown nav inNavbar className="alarm-nav-menu">
                    <DropdownToggle nav role="button" id="alarmDropdown">
                        <ModalShowNotifications
                            className="modal-ag-grid-tall"
                        />
                    </DropdownToggle>
                </UncontrolledDropdown>
            )}
            { error && <UncontrolledDropdown nav inNavbar className="alarm-nav-menu">
                <DropdownToggle nav role="button" id="alarmDropdown">
                    { (error as any).status !== 403 &&
                        <b>Alarms Request Error: {(error as any)?.originalError?.message || "Request Failed"}</b>
                    }
                    { (error as any).status === 403 &&
                        <b>Alarms Request Error: Permissions Error</b>
                    }
                </DropdownToggle>
            </UncontrolledDropdown>}
            <UncontrolledDropdown nav inNavbar className="utility-nav-menu">
                <DropdownToggle nav role="button" id="utilityDropdown">
                    <img src={SettingsIcon} alt="" style={{marginLeft: "20px"}}/>
                    {/*<span style={{"marginLeft": "2.5rem"}}>Settings</span>*/}
                    {totalBadgeCount > 0 && (
                        <Badge pill color="danger" className="pb-badge-text">
                            {totalBadgeCount}
                        </Badge>
                    )}
                </DropdownToggle>
                <DropdownMenu aria-labelledby="utilityDropdown">

                    { canViewSettingsPage(props.user, "general") &&
                        <SettingsNavMenuItem
                            page = "general"
                            label = "General"
                            history = { props.history}
                        />
                    }
                    { canViewSettingsPage(props.user, "instruments") &&
                        <SettingsNavMenuItem
                            page = "instruments"
                            label = "Instrument Settings"
                            history = { props.history}
                        />
                    }
                    { canViewSettingsPage(props.user, "notifications") &&
                        <SettingsNavMenuItem
                            page = "notifications"
                            label = "Notification Settings"
                            history = { props.history}
                        />
                    }
                    { canViewSettingsPage(props.user, "users") &&
                        <SettingsNavMenuItem
                            page = "users"
                            label = "User Management"
                            history = { props.history}
                        />
                    }
                    { canViewSettingsPage(props.user, "updates") &&
                         <SettingsNavMenuItem
                            history = { props.history}
                            page = "updates"
                            label = "Updates"
                        >
                            {aboutBadgeCount > 0 && (
                                <Badge
                                    pill
                                    color="danger"
                                    className="cog-badge"
                                    style={{ marginLeft: 0 }}
                                >
                                    {aboutBadgeCount}
                                </Badge>
                            )}
                        </SettingsNavMenuItem>
                    }
                    { canViewSettingsPage(props.user, "api-keys") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "api-keys"
                            label = "API Keys"
                        />
                    }
                    { canViewSettingsPage(props.user, "audit-logs") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "audit-logs"
                            label = "Audit Logs"
                        />
                    }
                    { canViewSettingsPage(props.user, "aws-analysis-settings") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "aws-analysis-settings"
                            label = "Analysis Settings"
                        />
                    }
                    { canViewSettingsPage(props.user, "set-instrument-login") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "set-instrument-login"
                            label = "Set Instrument Login"
                        />
                    }
                    { canViewSettingsPage(props.user, "instrument-login-management") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "instrument-login-management"
                            label = "Instrument Login Management"
                        />
                    }
                    { canViewSettingsPage(props.user, "register") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "register"
                            label = "Register"
                        />
                    }
                    { canViewSettingsPage(props.user, "about") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "about"
                            label = "About"
                        />
                    }
                    { canViewSettingsPage(props.user, "communications") &&
                        <SettingsNavMenuItem
                            history = { props.history}
                            page = "communications"
                            label = "Communication Preferences"
                        />
                    }

                </DropdownMenu>
            </UncontrolledDropdown>
        </Fragment>
    )
}
